import { Compound, VALID_DISPLAY_CANNABINOIDS } from '@/types';

/** Legacy Prismic Select values before `cannabinoid_display_order` became a Group. */
export type LegacyCannabinoidDisplayOrderSelect =
  | 'THC CBD TAC'
  | 'TAC THC CBD'
  | 'TAC CBD THC'
  | 'THC TAC CBD'
  | 'CBD TAC THC'
  | 'CBD THC TAC';

export type CannabinoidDisplayOrderGroupItem = {
  cannabinoid_type?: string | null;
};

export type CannabinoidDisplayOrderInput =
  | CannabinoidDisplayOrderGroupItem[]
  | LegacyCannabinoidDisplayOrderSelect
  | string;

/**
 * Runtime validator for cannabinoid strings coming from Prismic config or legacy select.
 * We must validate at runtime because `Compound` is a TypeScript-only type.
 */
const isDisplayCannabinoid = (value: string): value is Compound =>
  (VALID_DISPLAY_CANNABINOIDS as readonly string[]).includes(value);

/**
 * Normalizes user-entered / CMS-provided values to a canonical key.
 * Example: " thc " -> "THC".
 */
const canonicalizeCannabinoidType = (raw: string | null | undefined): string =>
  String(raw ?? '')
    .trim()
    .toUpperCase();

/**
 * Deduplicates while preserving the original order (first occurrence wins).
 */
const uniqPreserveOrder = <T>(arr: readonly T[]): T[] => {
  const seen = new Set<T>();
  const out: T[] = [];

  arr.forEach(item => {
    if (seen.has(item)) return;
    seen.add(item);
    out.push(item);
  });

  return out;
};

const parseGroupOrder = (
  rows: readonly CannabinoidDisplayOrderGroupItem[],
): Compound[] =>
  uniqPreserveOrder(
    rows
      .map(row => canonicalizeCannabinoidType(row?.cannabinoid_type))
      .filter((t): t is Compound => isDisplayCannabinoid(t)),
  );

const parseLegacyOrder = (legacy: string): Compound[] =>
  uniqPreserveOrder(
    legacy
      .split(' ')
      .map(t => canonicalizeCannabinoidType(t))
      .filter((t): t is Compound => isDisplayCannabinoid(t)),
  );

/**
 * Resolves the cannabinoid display order for product cards.
 *
 * Rules:
 * - If Prismic Group `cannabinoid_display_order` includes at least one valid cannabinoid,
 *   we display ONLY those (omitted = hidden) and in the same order (deduped).
 * - If the value is a legacy Select string, parse it by spaces.
 * - Otherwise fall back to the default `THC CBD TAC`.
 */
export const resolveCannabinoidDisplayOrder = (
  cannabinoidDisplayOrder?: CannabinoidDisplayOrderInput | null,
): Compound[] => {
  if (Array.isArray(cannabinoidDisplayOrder)) {
    const fromGroup = parseGroupOrder(cannabinoidDisplayOrder);

    if (fromGroup.length) {
      return fromGroup;
    }

    return parseLegacyOrder('THC CBD TAC');
  }

  if (typeof cannabinoidDisplayOrder === 'string') {
    return parseLegacyOrder(cannabinoidDisplayOrder);
  }

  return parseLegacyOrder('THC CBD TAC');
};
